import { useEffect, useState } from "react";
import { Icon } from "../../../components/ui/Icon";
import { useSearchParams } from "react-router-dom";
import { YoutubePlayer } from "../../../components/youtube/YoutubePlayer";
import LibrarySeries from "./LibrarySeries";
import {
  formatDuration,
  formatViewCount,
  useYoutubeLibrary,
  videoWatchUrl,
  VIDEO_SORT_LABELS,
  type VideoSort,
  type YoutubeLibraryVideo,
} from "../../../lib/youtube";

/**
 * Library → Videos. Same data as the public /youtube page, dressed in
 * the dashboard register: editor-friendly toolbar, sort, density toggle,
 * search. Read-only — no edit / delete affordances. Click a card to play
 * inline.
 */

const VALID_SORTS: VideoSort[] = ["newest", "oldest", "longest", "shortest", "popular", "alpha"];

type Density = "comfortable" | "compact";
const DENSITY_PAGE_SIZE: Record<Density, number> = {
  comfortable: 18,
  compact: 36,
};

export default function LibraryVideos() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") ?? "";
  const sortParam = params.get("sort") as VideoSort | null;
  const sort: VideoSort =
    sortParam && VALID_SORTS.includes(sortParam) ? sortParam : "newest";
  const density: Density =
    params.get("density") === "compact" ? "compact" : "comfortable";
  const offset = Math.max(0, Number(params.get("offset") ?? "0"));

  const [qInput, setQInput] = useState(q);
  useEffect(() => setQInput(q), [q]);

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value === null || value === "") next.delete(key);
    else next.set(key, value);
    if (key !== "offset") next.delete("offset");
    setParams(next, { replace: true });
  };

  const pageSize = DENSITY_PAGE_SIZE[density];
  const queryRes = useYoutubeLibrary({ limit: pageSize, offset, q, sort });
  const videos = queryRes.data?.videos ?? [];
  const total = queryRes.data?.total ?? 0;

  const onSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setParam("q", qInput.trim() || null);
  };

  return (
    <>
      {/* Collections first. Series are a way of GROUPING videos, not a separate
          kind of thing — they had their own tab, which made the reader choose
          between two doors into the same room. Leading with them also gives the
          page a shape: curated arcs, then the full catalogue underneath. */}
      <LibrarySeries />
      <section className="mt-14 border-t border-[var(--color-rule)] pt-10">
        <p className="page-eyebrow mb-1">Everything</p>
        <h2 className="font-display text-[1.7rem] sm:text-[2rem] leading-[1.15] tracking-tight">
          All recordings
        </h2>
        <p className="font-grotesk text-[0.78rem] text-[var(--color-ink-soft)] mt-2 max-w-[60ch]">
          Every video on the channel — classes, homas, festivals and live
          streams. Search, sort, or narrow the density to fit more on screen.
        </p>
      </section>
      <div className="yt-public-toolbar">
        <form onSubmit={onSearch} className="yt-search yt-public-search">
          <input
            type="search"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            placeholder="Search class titles, mantras, occasions…"
            aria-label="Search videos"
          />
          <button type="submit" className="btn-app-ghost">
            Search
          </button>
          {q && (
            <button
              type="button"
              className="row-action"
              onClick={() => {
                setQInput("");
                setParam("q", null);
              }}
            >
              Clear
            </button>
          )}
        </form>

        <div className="yt-public-controls">
          <label className="chip chip-select">
            <span className="chip-label">Sort</span>
            <select
              value={sort}
              onChange={(e) =>
                setParam("sort", e.target.value === "newest" ? null : e.target.value)
              }
              aria-label="Sort"
            >
              {VALID_SORTS.map((s) => (
                <option key={s} value={s}>
                  {VIDEO_SORT_LABELS[s]}
                </option>
              ))}
            </select>
          </label>
          <fieldset className="yt-density">
            <legend className="sr-only">Density</legend>
            <button
              type="button"
              className={`yt-density-btn ${density === "comfortable" ? "is-active" : ""}`}
              onClick={() => setParam("density", null)}
              aria-pressed={density === "comfortable"}
              title="Comfortable density"
            >
              <span aria-hidden>▢▢</span>
              <span className="sr-only">Comfortable</span>
            </button>
            <button
              type="button"
              className={`yt-density-btn ${density === "compact" ? "is-active" : ""}`}
              onClick={() => setParam("density", "compact")}
              aria-pressed={density === "compact"}
              title="Compact density"
            >
              <span aria-hidden>▢▢▢</span>
              <span className="sr-only">Compact</span>
            </button>
          </fieldset>
        </div>
      </div>

      <p className="yt-admin-count yt-public-count">
        {queryRes.isLoading ? (
          "Loading…"
        ) : total === 0 ? (
          q ? "No videos match." : "No videos yet."
        ) : (
          <>
            <span className="tabular">{total.toLocaleString()}</span>
            {q ? " match" : " video"}
            {total === 1 ? "" : q ? "es" : "s"} ·{" "}
            {VIDEO_SORT_LABELS[sort].toLowerCase()}
          </>
        )}
      </p>

      {queryRes.isLoading ? (
        <SkeletonGrid count={pageSize} density={density} />
      ) : videos.length === 0 ? null : (
        <ul className={`yt-public-grid is-${density}`}>
          {videos.map((v) => (
            <VideoCard key={v.id} video={v} />
          ))}
        </ul>
      )}

      <Pagination
        offset={offset}
        pageSize={pageSize}
        total={total}
        onChange={(o) => setParam("offset", o > 0 ? String(o) : null)}
      />
    </>
  );
}

function VideoCard({ video }: { video: YoutubeLibraryVideo }) {
  const [active, setActive] = useState(false);
  return (
    <li className="yt-card">
      <div className="yt-card-frame">
        {active ? (
          <YoutubePlayer kind="video" id={video.id} title={video.title} />
        ) : (
          <button
            type="button"
            className="yt-card-poster"
            onClick={() => setActive(true)}
            aria-label={`Play ${video.title}`}
          >
            <img
              src={video.thumbnailUrl}
              alt=""
              loading="lazy"
              decoding="async"
            />
            {video.durationSec ? (
              <span className="yt-card-duration tabular" aria-hidden>
                {formatDuration(video.durationSec)}
              </span>
            ) : null}
            <span className="yt-play" aria-hidden>
              <Icon name="play" size="1.3em" />
            </span>
          </button>
        )}
      </div>
      <div className="yt-card-meta">
        <a
          href={videoWatchUrl(video.id)}
          target="_blank"
          rel="noopener noreferrer"
          className="font-display text-[1.1rem] sm:text-[1.2rem] leading-[1.22] tracking-[-0.005em] hover:text-[var(--color-violet)] transition-colors"
        >
          {video.title}
        </a>
        <p className="font-grotesk text-[0.62rem] tracking-[0.18em] uppercase text-[var(--color-ink-mute)] mt-1.5">
          {video.publishedAt && formatPublished(video.publishedAt)}
          {video.viewCount !== null && video.viewCount !== undefined && (
            <>
              <span className="mx-2 text-[var(--color-ink-light)]">·</span>
              {formatViewCount(video.viewCount)} views
            </>
          )}
        </p>
      </div>
    </li>
  );
}

function Pagination({
  offset,
  pageSize,
  total,
  onChange,
}: {
  offset: number;
  pageSize: number;
  total: number;
  onChange: (offset: number) => void;
}) {
  if (total <= pageSize) return null;
  return (
    <nav className="yt-pagination yt-public-pagination" aria-label="Pagination">
      <button
        type="button"
        className="btn-ghost"
        onClick={() => onChange(Math.max(0, offset - pageSize))}
        disabled={offset <= 0}
      >
        <span>Newer</span>
      </button>
      <span className="font-grotesk text-[0.62rem] tracking-[0.18em] uppercase text-[var(--color-ink-mute)]">
        Page {Math.floor(offset / pageSize) + 1} of{" "}
        {Math.max(1, Math.ceil(total / pageSize))}
      </span>
      <button
        type="button"
        className="btn-ghost"
        onClick={() => onChange(offset + pageSize)}
        disabled={offset + pageSize >= total}
      >
        <span>Older</span>
      </button>
    </nav>
  );
}

function SkeletonGrid({
  count,
  density,
}: {
  count: number;
  density: Density;
}) {
  return (
    <ul className={`yt-public-grid is-${density}`}>
      {Array.from({ length: count }).map((_, i) => (
        <li key={i} className="yt-card">
          <div className="yt-card-frame">
            <div className="skel-block w-full h-full" aria-hidden />
          </div>
          <div className="yt-card-meta">
            <div
              className="skel-block"
              style={{ width: "82%", height: 16, marginBottom: 8 }}
            />
            <div className="skel-block" style={{ width: "40%", height: 10 }} />
          </div>
        </li>
      ))}
    </ul>
  );
}

function formatPublished(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
}
